import { Injectable } from '@angular/core';

/**
 * Service de préchargement des modules lazy-loaded critiques
 * Garantit que les chunks essentiels sont en cache AVANT le mode offline
 */
@Injectable({
  providedIn: 'root',
})
export class ModulePreloaderService {
  private preloadingInProgress = false;
  private preloadedModules = new Set<string>();

  /**
   * Précharger tous les modules critiques
   * À appeler au démarrage de l'app (après login)
   */
  async preloadCriticalModules(): Promise<void> {
    if (this.preloadingInProgress) {
      return;
    }

    this.preloadingInProgress = true;
    const startTime = performance.now();

    try {
      // Précharger en parallèle les modules les plus utilisés
      const preloadPromises = [
        this.preloadModule('dashboard', () => import('../../features/dashboard/dashboard.routes')),
        this.preloadModule('visits', () => import('../../features/visits/visits.routes')),
        this.preloadModule('clients', () => import('../../features/clients/clients.routes')),
        this.preloadModule('orders', () => import('../../features/orders/orders.routes')),
        this.preloadModule('shippings', () => import('../../features/shippings/shippings.routes')),
        this.preloadModule('settings', () => import('../../features/settings/settings.routes')),
      ];

      await Promise.all(preloadPromises);

      const duration = Math.round(performance.now() - startTime);
    } catch (error) {
      console.warn('⚠️ Some modules failed to preload (may already be cached):', error);
    } finally {
      this.preloadingInProgress = false;
    }
  }

  /**
   * Précharger un module spécifique
   */
  private async preloadModule(name: string, importFn: () => Promise<any>): Promise<void> {
    if (this.preloadedModules.has(name)) {
      return;
    }

    try {
      await importFn();
      this.preloadedModules.add(name);
    } catch (error) {
      console.warn(`  ✗ Failed to preload module '${name}':`, error);
      throw error;
    }
  }

  /**
   * Vérifier si un module a été préchargé
   */
  isModulePreloaded(name: string): boolean {
    return this.preloadedModules.has(name);
  }

  /**
   * Obtenir la liste des modules préchargés
   */
  getPreloadedModules(): string[] {
    return Array.from(this.preloadedModules);
  }
}
